import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { SlidersHorizontal, Zap } from 'lucide-react';
import { usePageMeta, prefersReducedMotion, Reveal, Count } from '../lib/site';
import { CardHeader, CtaBand } from '../components/Bits';
import PortalFrame from '../components/PortalFrame';
import Calculator from '../components/Calculator';

export default function Home() {
  usePageMeta(
    'MyGolfLinks | More golfers for your course. None of the games.',
    'LinksOS runs your tee sheet and the MyGolfLinks app fills it with local golfers. One flat monthly plan, small published fees, no barter, no parity clauses.'
  );

  const heroRef = useRef(null);
  const onMove = (e) => {
    if (prefersReducedMotion()) return;
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    el.style.setProperty('--px', `${(x * 10).toFixed(1)}px`);
    el.style.setProperty('--py', `${(y * 7).toFixed(1)}px`);
  };

  const card = (k) => ({
    transform: `translate(calc(var(--px, 0px) * ${k}), calc(var(--py, 0px) * ${k}))`,
    transition: 'transform 0.3s var(--ease)',
  });

  return (
    <main>
      {/* ── Hero ── */}
      <section
        ref={heroRef}
        onMouseMove={onMove}
        style={{ position: 'relative', overflow: 'hidden', padding: 'clamp(140px, 16vw, 190px) 0 clamp(60px, 7vw, 90px)' }}
      >
        <div className="wrap" style={{ position: 'relative' }}>
          <div className="hero-split">
            <div>
              <p className="hl mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 22 }}>
                Golf technology · Built in Indiana
              </p>
          <h1 className="display" style={{ maxWidth: 760 }}>
            <span className="hl" style={{ display: 'block' }}>Your tee sheet.</span>
            <span className="hl" style={{ display: 'block', animationDelay: '0.08s' }}>Your prices.</span>
            <span className="hl" style={{ display: 'block', animationDelay: '0.16s', color: 'var(--fairway)' }}>Your customers.</span>
          </h1>
          <p className="lede hl" style={{ margin: '26px 0 34px', animationDelay: '0.26s' }}>
            LinksOS runs your tee sheet, and the MyGolfLinks app fills it with local golfers. One
            flat monthly plan, small fees we publish, and none of the games the booking platforms
            taught you to expect.
          </p>
          <div className="hl" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', animationDelay: '0.32s' }}>
            <Link to="/demo" className="btn btn-gold">Book a demo</Link>
            <Link to="/courses" className="btn btn-line">See how it works</Link>
          </div>
              <p className="hl" style={{ marginTop: 18, fontSize: '0.9rem', color: 'var(--mut)', animationDelay: '0.38s' }}>
                Golfer, not a GM? <Link to="/golfers" className="tlink">Get the app</Link>
              </p>
            </div>

            <div className="hl" style={{ ...card(0.6), animationDelay: '0.4s' }}>
              <PortalFrame />
              <p className="mono" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--faint)', marginTop: 12, textAlign: 'center' }}>
                LinksOS · the owner portal · sample data
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stat band: the honest numbers ── */}
      <section className="band-pine on-pine">
        <div className="wrap" style={{ padding: 'clamp(52px, 7vw, 80px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal className="stats">
            <div>
              <div className="stat-v">$<Count value={197} /></div>
              <div className="stat-l">a month, everything included. Plans start here, no add-on modules.</div>
            </div>
            <div>
              <div className="stat-v">$<Count value={1.8} decimals={2} /></div>
              <div className="stat-l">flat booking fee, only on app bookings. Your own website stays fee-free.</div>
            </div>
            <div>
              <div className="stat-v">$<Count value={0} /></div>
              <div className="stat-l">your fee on supplier orders. The supplier pays the platform fee, not you.</div>
            </div>
            <div>
              <div className="stat-v">0</div>
              <div className="stat-l">barter rounds, parity clauses, or surprise fees at your golfer's checkout.</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── The loop ── */}
      <section>
        <div className="wrap" style={{ padding: 'clamp(70px, 9vw, 110px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="01" label="How it works" />
            <h2 className="h2" style={{ maxWidth: 620 }}>Demand first. Everything else is depth.</h2>
          </Reveal>
          <Reveal className="ledger" style={{ marginTop: 42 }}>
            <div className="led">
              <span className="led-no">1</span>
              <span className="led-title">Golfers find you in the app</span>
              <span className="led-body">
                MyGolfLinks players browse courses near them, see your real rates, and book in a few
                taps. One flat $1.80 booking fee, shown upfront, and only in the app. Booking on
                your own website costs them nothing extra.
              </span>
            </div>
            <div className="led">
              <span className="led-no">2</span>
              <span className="led-title">Your sheet fills at your price</span>
              <span className="led-body">
                You set rack and app rates. Turn on dynamic pricing and the engine works inside a
                floor and ceiling you control. Fire a Flash Deal when you want a slow afternoon moved.
              </span>
            </div>
            <div className="led">
              <span className="led-no">3</span>
              <span className="led-title">The golfer stays yours</span>
              <span className="led-body">
                Every booking lands with the golfer's name attached. No middleman between you and
                the person standing on your first tee, and no one reselling your inventory back to
                your own market.
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── The platform ── */}
      <section className="band-card">
        <div className="wrap" style={{ padding: 'clamp(70px, 9vw, 110px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="02" label="One platform" />
            <h2 className="h2" style={{ maxWidth: 640 }}>Three products, one connected loop</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              MyGolfLinks is the company and the platform. Golfers know it as the MyGolfLinks app,
              courses run LinksOS, and suppliers and brands work in the MGL Partner Portal.
            </p>
          </Reveal>
          <Reveal className="ledger" style={{ marginTop: 42 }}>
            <div className="led">
              <span className="led-no mono">App</span>
              <span className="led-title">The MyGolfLinks app, for golfers</span>
              <span className="led-body">
                Tee times and course deals, GPS rounds with scoring and stats, a feed with friends,
                and a shop with real offers from brands. Free, and it's where your demand comes from.{' '}
                <Link to="/golfers" className="tlink">For golfers</Link>
              </span>
            </div>
            <div className="led">
              <span className="led-no mono">OS</span>
              <span className="led-title">LinksOS, for owners and operators</span>
              <span className="led-body">
                Your tee sheet and the rest of the operation: rate engine, dynamic pricing, Flash
                Deals, an in-house point of sale with split bill and pay-from-phone, staff
                scheduling, issue tracking, and supplier ordering.{' '}
                <Link to="/courses" className="tlink">For courses</Link>
              </span>
            </div>
            <div className="led">
              <span className="led-no mono">MGL</span>
              <span className="led-title">MGL Partner Portal, for suppliers and brands</span>
              <span className="led-body">
                Catalogs, ordering, and business messaging with courses. Brands can also sell
                directly to players through the app shop.{' '}
                <Link to="/partners" className="tlink">For partners</Link>
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Two levers ── */}
      <section className="band-pine on-pine">
        <div className="wrap" style={{ padding: 'clamp(70px, 9vw, 110px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="03" label="Two pricing levers" dark />
            <h2 className="h2" style={{ maxWidth: 640 }}>One is automated. One is a button you press.</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              Both are included in the plan, and the difference matters.
            </p>
          </Reveal>
          <Reveal className="lever-grid" style={{ marginTop: 40 }} d={1}>
            <div style={{ border: '1px solid var(--cream-line)', borderRadius: 14, padding: '26px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <SlidersHorizontal size={19} aria-hidden="true" style={{ color: 'var(--gold-bright)' }} />
                <span className="mono" style={{ fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cream-mut)' }}>
                  Automated
                </span>
              </div>
              <h3 className="h3">Dynamic pricing you control</h3>
              <p style={{ color: 'var(--cream-mut)', fontSize: '0.95rem', marginTop: 10 }}>
                You set a floor and a ceiling. The engine moves the price inside that band with real
                demand: weather, how full the sheet is, time to the tee time. It never leaves your
                range. You control the band, it optimizes inside it.
              </p>
            </div>
            <div style={{ border: '1px solid var(--cream-line)', borderRadius: 14, padding: '26px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Zap size={19} aria-hidden="true" style={{ color: 'var(--flag)' }} />
                <span className="mono" style={{ fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cream-mut)' }}>
                  Manual
                </span>
              </div>
              <h3 className="h3">Flash Deals you fire</h3>
              <p style={{ color: 'var(--cream-mut)', fontSize: '0.95rem', marginTop: 10 }}>
                A course-initiated, time-boxed discount for the inventory you pick. It pushes to
                real golfers in the app, runs for the window you set, and ends. It never fires on
                its own.
              </p>
            </div>
          </Reveal>
          <Reveal d={2} style={{ marginTop: 30 }}>
            <Link to="/courses#demo" className="tlink">Try both in the live LinksOS sandbox</Link>
          </Reveal>
        </div>
      </section>

      {/* ── The math ── */}
      <section>
        <div className="wrap" style={{ padding: 'clamp(70px, 9vw, 110px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="04" label="The math" />
            <h2 className="h2" style={{ maxWidth: 620 }}>What does trading tee times actually cost?</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              Set the sliders to your numbers. The arithmetic is shown, so check it.
            </p>
          </Reveal>
          <Reveal style={{ marginTop: 44 }} d={1}>
            <Calculator compact />
          </Reveal>
        </div>
      </section>

      {/* ── Founding program ── */}
      <section className="band-card">
        <div className="wrap" style={{ padding: 'clamp(70px, 9vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal style={{ maxWidth: 720 }}>
            <CardHeader no="05" label="Founding courses" />
            <h2 className="h2">We're early. That's the offer.</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              MyGolfLinks is onboarding a founding group of Indiana courses right now, and the
              founding deal is simple: $400 once, not a monthly bill, and the platform is yours
              forever. Founding courses also get a direct line to the people building the
              product, and what they ask for shapes what ships.
            </p>
            <div style={{ marginTop: 26, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/demo" className="btn btn-gold">Book a demo</Link>
              <Link to="/founding" className="btn btn-line">The founding deal</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="See it on your own numbers"
        sub="Thirty minutes. We start with the demand side, walk the parts of LinksOS you care about, and put your numbers in the calculator live."
      />
    </main>
  );
}
